'use client'

import { useEffect } from 'react'
import { track, type CtaLocation } from '@/lib/analytics'

/**
 * Every link on the site, counted from one listener.
 *
 * The alternative is an `onClick` on each anchor, and the cost of that is not
 * the typing: the header, the footer, the CTA bands and every card are server
 * components, and giving them a handler turns the whole navigation into client
 * components for the sake of one push. A listener on the document sees the
 * same clicks and leaves them on the server.
 *
 * What a link *is* it says itself — `mailto:`, `tel:`, another origin. What it
 * *means* it cannot, so a call to action carries `data-cta` with the place it
 * sits, and the label falls back to the words the visitor read.
 */
export function ClickTracking() {
    useEffect(() => {
        function onClick(event: MouseEvent) {
            const target = event.target

            if (!(target instanceof Element)) {
                return
            }

            const link = target.closest('a')

            if (link === null) {
                return
            }

            // The attribute, not the property: `href` resolves to an absolute
            // URL, which loses the difference between a path and a scheme.
            const href = link.getAttribute('href') ?? ''
            const text = (link.textContent ?? '').trim().slice(0, 100)

            if (href.startsWith('mailto:')) {
                track({
                    event: 'contact_click',
                    contact_method: 'email',
                    contact_value: href.slice('mailto:'.length),
                })

                return
            }

            if (href.startsWith('tel:')) {
                track({
                    event: 'contact_click',
                    contact_method: 'phone',
                    contact_value: href.slice('tel:'.length),
                })

                return
            }

            const cta = link.dataset.cta

            if (cta !== undefined) {
                track({
                    event: 'cta_click',
                    cta_label: link.dataset.ctaLabel ?? text,
                    cta_location: cta as CtaLocation,
                    cta_destination: href,
                })

                return
            }

            // Anything that leaves this origin, including a protocol-relative
            // URL, which reads as a path and is not one. `URL.parse` would say
            // this without a try, and is newer than the browsers this site
            // supports.
            let destination: URL

            try {
                destination = new URL(href, window.location.href)
            } catch {
                return
            }

            if (destination.origin !== window.location.origin) {
                track({
                    event: 'outbound_click',
                    link_url: destination.href,
                    link_domain: destination.hostname,
                    link_text: text,
                })
            }
        }

        // Capture, so a handler that stops propagation — the mobile menu
        // closes on its own links — does not take the click with it.
        document.addEventListener('click', onClick, true)

        return () => document.removeEventListener('click', onClick, true)
    }, [])

    return null
}
